import React, { useState, useEffect, useRef } from "react";
import heroImage from "../assets/hero.jpg";
import techImage from "../assets/tech.jpg";

function CountUp({ end, duration = 2000, suffix = "" }) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          let startTimestamp = null;
          const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) {
              window.requestAnimationFrame(step);
            } else {
              setCount(end);
            }
          };
          window.requestAnimationFrame(step);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = elementRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [end, duration]);

  return <span ref={elementRef}>{count}{suffix}</span>;
}

export default function AboutPage() {
  return (
    <section className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-800 font-sans">
      
      {/* Hero Header Banner */}
      <div
        className="relative bg-cover bg-center text-white py-16 sm:py-28 px-4 sm:px-6 text-center shadow-lg"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]"></div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 tracking-tight drop-shadow-md">
            About Atronm
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-slate-100 max-w-2xl mx-auto drop-shadow-sm font-light">
            Building smarter, scalable solutions with AI, Web, Cloud & Custom Development.
          </p>
        </div>
      </div>

      {/* Who We Are Section */}
      <div className="max-w-6xl mx-auto py-20 px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div data-aos="fade-right" className="space-y-6">
            <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider rounded-full">
              Our Identity
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Who We Are
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              <strong className="text-slate-900 font-semibold">Atronm</strong> is a future-focused technology firm delivering intelligent digital solutions in 
              <span className="text-blue-600 font-semibold"> AI/ML, Cloud, Web & Custom Software</span>. We blend innovation with execution, offering world-class tech solutions to startups, SMEs, and enterprises worldwide.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We leverage modern technology frameworks and deep domain expertise to build software that is secure, performance-optimized, and engineered for long-term scalability.
            </p>
          </div>
          <div data-aos="fade-left" className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
            <img 
              src={techImage} 
              alt="Tech Illustration" 
              className="relative rounded-lg shadow-xl w-full object-cover transition-transform duration-500 hover:scale-[1.01]" 
              loading="eager"
              fetchPriority="high"
              decoding="sync"
            />
          </div>
        </div>
      </div>

      {/* What We Achieved / How We Achieve Success Section */}
      <div className="max-w-6xl mx-auto pb-20 px-6">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          {/* Left text column (5/12 width on desktop) */}
          <div className="md:col-span-5 space-y-6" data-aos="fade-right">
            <div className="inline-block text-blue-600 text-xs font-bold uppercase tracking-widest">
              What We Achieved
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
              How We Achieve Success
            </h2>
            <p className="text-gray-650 leading-relaxed font-light">
              Through strategy, design, and planning, we create digital solutions that connect with your audience. We then fine-tune our development plans to allow for a focused approach, ensuring that every effort is aligned with your business goals.
            </p>
          </div>

          {/* Right cards grid (7/12 width on desktop) */}
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6" data-aos="fade-left">
            <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 text-center transition-all duration-300 hover:shadow-md hover:bg-white">
              <div className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-2">
                <CountUp end={5} suffix="+" />
              </div>
              <div className="text-xs sm:text-sm text-slate-500 font-semibold uppercase tracking-wider">
                Total Top Services
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 text-center transition-all duration-300 hover:shadow-md hover:bg-white">
              <div className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-2">
                <CountUp end={12} suffix="+" />
              </div>
              <div className="text-xs sm:text-sm text-slate-500 font-semibold uppercase tracking-wider">
                Countries
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 text-center transition-all duration-300 hover:shadow-md hover:bg-white">
              <div className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-2">
                <CountUp end={99} suffix="%" />
              </div>
              <div className="text-xs sm:text-sm text-slate-500 font-semibold uppercase tracking-wider">
                Positive Feedback
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 text-center transition-all duration-300 hover:shadow-md hover:bg-white">
              <div className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-2">
                <CountUp end={15} suffix="+" />
              </div>
              <div className="text-xs sm:text-sm text-slate-500 font-semibold uppercase tracking-wider">
                Usual Users
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission & Vision Section */}
      <div className="bg-slate-50 border-y border-slate-100 py-20 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10">
          <div 
            className="p-8 bg-white border border-slate-100 shadow-md rounded-2xl transition-all duration-300 hover:shadow-lg flex flex-col items-center text-center relative overflow-hidden" 
            data-aos="zoom-in"
          >
            {/* Top gradient accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-purple-500 to-indigo-500"></div>
            
            <div className="inline-flex items-center justify-center px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-purple-700 bg-purple-50 rounded-full mb-6 mt-2">
              Core Purpose
            </div>
            <h3 className="text-2xl font-bold text-slate-950 mb-3">Our Mission</h3>
            <p className="text-gray-600 leading-relaxed max-w-md">
              To engineer digital solutions that empower innovation and accelerate success in an AI-driven world.
            </p>
          </div>
          
          <div 
            className="p-8 bg-white border border-slate-100 shadow-md rounded-2xl transition-all duration-300 hover:shadow-lg flex flex-col items-center text-center relative overflow-hidden" 
            data-aos="zoom-in" 
            data-aos-delay="100"
          >
            {/* Top gradient accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 to-teal-500"></div>
            
            <div className="inline-flex items-center justify-center px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 bg-blue-50 rounded-full mb-6 mt-2">
              Long-term Impact
            </div>
            <h3 className="text-2xl font-bold text-slate-950 mb-3">Our Vision</h3>
            <p className="text-gray-600 leading-relaxed max-w-md">
              To become a globally trusted tech partner, recognized for our commitment to innovation, integrity, and real-world impact.
            </p>
          </div>
        </div>
      </div>

      {/* Why Choose Atronm Section */}
      <div className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16" data-aos="fade-down">
            <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-3">
              Why Partner With Us
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Why Choose Atronm?
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-4">
              We design, build, and deploy systems that align perfectly with your organizational goals and accelerate growth.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
            <div 
              className="p-8 bg-slate-50 border border-slate-100 rounded-2xl shadow-sm hover:bg-white hover:border-purple-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(168,85,247,0.12)] transition-all duration-300 group" 
              data-aos="fade-up"
            >
              <div className="relative inline-block mb-4">
                <div className="text-3xl font-extrabold text-slate-300 group-hover:text-purple-600 transition-colors duration-300 font-mono">
                  01
                </div>
                <div className="absolute -bottom-1 left-0 w-full h-[2px] bg-purple-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">Security-First Approach</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                We prioritize user privacy and data security in every piece of code we write.
              </p>
            </div>

            <div 
              className="p-8 bg-slate-50 border border-slate-100 rounded-2xl shadow-sm hover:bg-white hover:border-blue-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(59,130,246,0.12)] transition-all duration-300 group" 
              data-aos="fade-up" 
              data-aos-delay="100"
            >
              <div className="relative inline-block mb-4">
                <div className="text-3xl font-extrabold text-slate-300 group-hover:text-blue-600 transition-colors duration-300 font-mono">
                  02
                </div>
                <div className="absolute -bottom-1 left-0 w-full h-[2px] bg-blue-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">Performance Optimized</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Supercharged speeds, zero bloat, and highly efficient architectures.
              </p>
            </div>

            <div 
              className="p-8 bg-slate-50 border border-slate-100 rounded-2xl shadow-sm hover:bg-white hover:border-green-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(34,197,94,0.12)] transition-all duration-300 group" 
              data-aos="fade-up" 
              data-aos-delay="200"
            >
              <div className="relative inline-block mb-4">
                <div className="text-3xl font-extrabold text-slate-300 group-hover:text-green-600 transition-colors duration-300 font-mono">
                  03
                </div>
                <div className="absolute -bottom-1 left-0 w-full h-[2px] bg-green-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">AI-Driven Development</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Integrating predictive analytics and intelligence to automate work.
              </p>
            </div>

            <div 
              className="p-8 bg-slate-50 border border-slate-100 rounded-2xl shadow-sm hover:bg-white hover:border-red-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(239,68,68,0.12)] transition-all duration-300 group" 
              data-aos="fade-up" 
              data-aos-delay="300"
            >
              <div className="relative inline-block mb-4">
                <div className="text-3xl font-extrabold text-slate-300 group-hover:text-red-600 transition-colors duration-300 font-mono">
                  04
                </div>
                <div className="absolute -bottom-1 left-0 w-full h-[2px] bg-red-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">Long-Term Tech Partner</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                We support your journey from idea validation to scale and maintenance.
              </p>
            </div>

            <div 
              className="p-8 bg-slate-50 border border-slate-100 rounded-2xl shadow-sm hover:bg-white hover:border-yellow-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(234,179,8,0.12)] transition-all duration-300 group" 
              data-aos="fade-up" 
              data-aos-delay="400"
            >
              <div className="relative inline-block mb-4">
                <div className="text-3xl font-extrabold text-slate-300 group-hover:text-yellow-600 transition-colors duration-300 font-mono">
                  05
                </div>
                <div className="absolute -bottom-1 left-0 w-full h-[2px] bg-yellow-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">Innovation-Driven</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Consistently experimenting with state-of-the-art tech and practices.
              </p>
            </div>

            <div 
              className="p-8 bg-slate-50 border border-slate-100 rounded-2xl shadow-sm hover:bg-white hover:border-indigo-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(99,102,241,0.12)] transition-all duration-300 group" 
              data-aos="fade-up" 
              data-aos-delay="500"
            >
              <div className="relative inline-block mb-4">
                <div className="text-3xl font-extrabold text-slate-300 group-hover:text-indigo-600 transition-colors duration-300 font-mono">
                  06
                </div>
                <div className="absolute -bottom-1 left-0 w-full h-[2px] bg-indigo-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">Agile & On-Time Delivery</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Iterative sprint delivery ensures visibility and meeting all deadlines.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Work Process Section */}
      <div className="py-20 px-6 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16" data-aos="fade-down">
            <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-3">
              How We Deliver
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Our Development Process
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-4">
              We follow a structured, collaborative methodology to turn your concept into a robust digital reality.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 relative group" data-aos="fade-up">
              <div className="absolute top-4 right-6 text-5xl font-extrabold text-slate-100 group-hover:text-slate-200 transition-colors duration-300">01</div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 mt-4">Discovery & Scope</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                We sit down to analyze your goals, outline requirements, map user stories, and define clear project scopes.
              </p>
            </div>

            <div className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 relative group" data-aos="fade-up" data-aos-delay="100">
              <div className="absolute top-4 right-6 text-5xl font-extrabold text-slate-100 group-hover:text-slate-200 transition-colors duration-300">02</div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 mt-4">UI/UX & Architecture</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                We craft beautiful user interfaces and map out database models and microservices before writing the first line of code.
              </p>
            </div>

            <div className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 relative group" data-aos="fade-up" data-aos-delay="200">
              <div className="absolute top-4 right-6 text-5xl font-extrabold text-slate-100 group-hover:text-slate-200 transition-colors duration-300">03</div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 mt-4">Agile Development</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Our developers build core logic and features using clean code principles and iterative sprints.
              </p>
            </div>

            <div className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 relative group" data-aos="fade-up" data-aos-delay="300">
              <div className="absolute top-4 right-6 text-5xl font-extrabold text-slate-100 group-hover:text-slate-200 transition-colors duration-300">04</div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 mt-4">Deployment & Care</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                After comprehensive quality checks, we deploy your app and offer continuous optimization and maintenance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Global Operations Section */}
      <div className="bg-[#0b0a1e] text-white py-20 px-6 text-center">
        <div className="max-w-4xl mx-auto space-y-4">
          <h2 className="text-3xl font-bold tracking-tight">Global Operations</h2>
          <p className="text-slate-300 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Atronm operates on a global scale, serving clients internationally with distributed remote teams synced across multiple time zones.
          </p>
        </div>
      </div>
    </section>
  );
}
